import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { GroupMember } from '../types';
import { useAuth } from '../../../contexts/AuthContext';

interface MemberManagementTableProps {
  members: GroupMember[];
  onRemoveMember: (memberId: string) => void;
  onChangeRole: (memberId: string, newRole: 'admin' | 'member' | 'clerk') => void;
  onRegisterMember?: () => void;
  onImportMembers?: () => void;
  onPayMembershipFee?: (memberId: string, amount: number) => void;
  groupMembershipFee?: number;
  groupCreatorId?: string; // ID of the user who created the group
}

const MemberManagementTable = ({
  members,
  onRemoveMember,
  onChangeRole,
  onRegisterMember,
  onImportMembers,
  onPayMembershipFee,
  groupMembershipFee = 0,
  groupCreatorId,
}: MemberManagementTableProps) => {
  const { userProfile } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'admin' | 'member' | 'clerk'>('all');
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [paymentModal, setPaymentModal] = useState<{ memberId: string, name: string } | null>(null);
  const [paymentAmount, setPaymentAmount] = useState<string>(groupMembershipFee.toString());

  // Super_admin can change all roles (including group_creator)
  // Group_creator can change roles of OTHER members (not their own)
  // Disable role changes only for group_admin
  // Assigned admins (group_members.role='admin') can also change roles
  const userRole = userProfile?.role;
  const userId = userProfile?.id;
  const isSuperAdmin = userRole === 'super_admin';
  const isGroupAdmin = userRole === 'group_admin';
  const isGroupCreator = userRole === 'group_creator';
  
  // Super_admin can always change roles
  // Group_admin cannot change roles
  // Group_creator and assigned admins can change other members' roles
  let canChangeRoles = false;
  if (isSuperAdmin) {
    canChangeRoles = true; // Super_admin can always change roles
  } else if (isGroupAdmin) {
    canChangeRoles = false; // Group_admin cannot change roles
  } else if (isGroupCreator || userProfile?.group_roles?.includes('admin')) {
    canChangeRoles = true; // Group_creator and assigned admins can change roles
  }
  
  // Debug logging
  if (process.env.NODE_ENV === 'development') {
    console.log('[MemberManagementTable] Role check:', {
      userRole,
      userId,
      isSuperAdmin,
      isGroupAdmin,
      isGroupCreator,
      canChangeRoles,
    });
  }

  const filteredMembers = members.filter((member) => {
    const matchesSearch =
      member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || member.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const handleRemoveClick = (memberId: string) => {
    setConfirmRemove(memberId);
  };

  const handleConfirmRemove = () => {
    if (confirmRemove) {
      onRemoveMember(confirmRemove);
      setConfirmRemove(null);
    }
  };

  return (
    <div className="bg-card rounded-md border border-border shadow-elevation-1">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-secondary/10 rounded-md flex items-center justify-center">
              <Icon name="Users" size={24} color="var(--color-secondary)" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Member Management</h2>
              <p className="text-sm text-muted-foreground">
                {filteredMembers.length} of {members.length} members
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            {onImportMembers && (
              <Button
                variant="outline"
                iconName="Upload"
                iconPosition="left"
                onClick={onImportMembers}
              >
                Import CSV
              </Button>
            )}
          {onRegisterMember && (
            <Button
              variant="default"
              iconName="UserPlus"
              iconPosition="left"
              onClick={onRegisterMember}
            >
              Register Member
            </Button>
          )}
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Icon
              name="Search"
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="text"
              placeholder="Search members..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-input rounded-sm bg-background text-foreground"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value as 'all' | 'admin' | 'member' | 'clerk')}
            className="px-4 py-2 border border-input rounded-sm bg-background text-foreground"
          >
            <option value="all">All Roles</option>
            <option value="admin">Admins</option>
            <option value="clerk">Clerks</option>
            <option value="member">Members</option>
          </select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Member
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Join Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Contributions
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Membership Fee
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredMembers.map((member) => (
              <tr key={member.id} className="hover:bg-muted/50 transition-interactive">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <Image
                        src={member.avatar}
                        alt={member.alt}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground">{member.name}</div>
                      <div className="text-xs text-muted-foreground">{member.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  {/* Show "Group Creator" badge if member is the group creator */}
                  {/* Super_admin can still change the role via dropdown */}
                  {member.id === groupCreatorId ? (
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center px-3 py-1 rounded-sm bg-primary/10 text-primary text-sm font-medium border border-primary/20">
                        Group Creator
                      </span>
                      {/* Super_admin can change group creator's role */}
                      {isSuperAdmin && (
                        <select
                          value={member.role}
                          onChange={(e) => {
                            onChangeRole(member.id, e.target.value as 'admin' | 'member' | 'clerk');
                          }}
                          className="px-2 py-1 border border-input rounded-sm bg-background text-foreground text-xs cursor-pointer"
                          title="Super Admin: Change group creator's role"
                        >
                          <option value="admin">Admin</option>
                          <option value="clerk">Clerk</option>
                          <option value="member">Member</option>
                        </select>
                      )}
                    </div>
                  ) : (
                    <select
                      value={member.role}
                      onChange={(e) => {
                        if (canChangeRoles) {
                          onChangeRole(member.id, e.target.value as 'admin' | 'member' | 'clerk');
                        } else {
                          console.warn('[MemberManagementTable] Role change blocked:', {
                            userRole,
                            memberId: member.id,
                            memberName: member.name,
                          });
                        }
                      }}
                      disabled={!canChangeRoles}
                      className={`px-3 py-1 border border-input rounded-sm bg-background text-foreground text-sm ${
                        !canChangeRoles ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                      }`}
                      title={!canChangeRoles ? 'Group Admin cannot change member roles. Super Admin, Group Creator, and assigned group admins can change roles.' : isSuperAdmin ? 'Super Admin: Change member role' : 'Change member role'}
                    >
                      <option value="admin">Admin</option>
                      <option value="clerk">Clerk</option>
                      <option value="member">Member</option>
                    </select>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-foreground">
                  {member.joinDate.toLocaleDateString()}
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-foreground">
                    {member.totalContributions.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} VT
                  </div>
                  {member.lastContribution && (
                    <div className="text-xs text-muted-foreground">
                      Last: {member.lastContribution.toLocaleDateString()}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4">
                  {groupMembershipFee > 0 ? (
                    <div className="flex flex-col gap-1">
                      <span
                        className={`inline-flex items-center w-fit px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider ${
                          member.membershipFeeStatus === 'fully_paid' 
                            ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' 
                            : member.membershipFeeStatus === 'partially_paid'
                            ? 'bg-amber-100 text-amber-700 border border-amber-200'
                            : 'bg-destructive/10 text-destructive border border-destructive/20'
                        }`}
                      >
                        {member.membershipFeeStatus === 'fully_paid' ? 'Full Paid' : member.membershipFeeStatus === 'partially_paid' ? 'Partial' : 'Unpaid'}
                      </span>
                      <div className="text-xs text-muted-foreground">
                        {member.membershipFeePaid?.toLocaleString() || 0} / {groupMembershipFee.toLocaleString()} VT
                      </div>
                      {onPayMembershipFee && member.membershipFeeStatus !== 'fully_paid' ? (
                        <button
                          onClick={() => {
                            setPaymentModal({ memberId: member.id, name: member.name });
                            setPaymentAmount((groupMembershipFee - (member.membershipFeePaid || 0)).toString());
                          }}
                          className="text-[10px] text-primary font-semibold hover:underline text-left"
                        >
                          Record Payment
                        </button>
                      ) : member.membershipFeeStatus === 'fully_paid' ? (
                        <div className="flex items-center gap-1 text-[10px] text-emerald-600 font-bold">
                          <Icon name="Check" size={12} />
                          COMPLETE
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground italic">No fee set</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-sm text-xs font-medium ${
                      member.status === 'active' ?'bg-success/10 text-success' :'bg-muted text-muted-foreground'
                    }`}
                  >
                    {member.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Trash2"
                    onClick={() => handleRemoveClick(member.id)}
                  >
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredMembers.length === 0 && (
        <div className="p-12 text-center">
          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No members found</p>
        </div>
      )}

      {confirmRemove && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1200 p-4">
          <div className="bg-card rounded-md shadow-elevation-2 p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold text-foreground mb-2">Remove member?</h3>
                <p className="text-sm text-muted-foreground mb-6">
              This will remove the member from the group. You can add them back later if needed.
                </p>
                <div className="flex gap-3 justify-end">
                  <Button variant="outline" onClick={() => setConfirmRemove(null)}>
                    Cancel
                  </Button>
              <Button variant="default" onClick={handleConfirmRemove}>
                    Remove
                  </Button>
                </div>
          </div>
        </div>
      )}

      {paymentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1200 p-4">
          <div className="bg-card rounded-md shadow-elevation-2 p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold text-foreground mb-4">Record Membership Fee: {paymentModal.name}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">Amount Paid (VT)</label>
                <input
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-sm bg-background text-foreground"
                />
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <Button variant="outline" onClick={() => setPaymentModal(null)}>
                  Cancel
                </Button>
                <Button 
                  variant="default" 
                  onClick={() => {
                    if (onPayMembershipFee) {
                      onPayMembershipFee(paymentModal.memberId, parseFloat(paymentAmount));
                    }
                    setPaymentModal(null);
                  }}
                >
                  Record Payment
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberManagementTable;